/**
 * Session 60.22 · ZipPromptBanner
 *
 * Lightweight nudge for members whose profile is missing `postal_code`.
 * Copy verbatim per Cam's spec:
 *
 *   "Find members and workspaces near you
 *    Add your ZIP code to personalize what's nearby."
 *
 *   CTA: Add ZIP Code   ·   Secondary: Not now
 *
 * Rules:
 *   · Only renders when the current user is missing `postal_code`.
 *   · Dismissible for 14 days via localStorage (does not block Community).
 *   · CTA opens /profile#zip (reuses existing ZIP auto-detect field which
 *     already handles the "Use my location" style flow at that spot).
 *   · Zero email sends — this is a UI nudge only.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";

const LS_KEY = "metier_zip_prompt_dismissed_at";
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isRecentlyDismissed() {
  try {
    const raw = Number(localStorage.getItem(LS_KEY) || 0);
    return raw > 0 && Date.now() - raw < DISMISS_WINDOW_MS;
  } catch {
    return false;
  }
}

export default function ZipPromptBanner({ variant = "default" }) {
  const { user } = useAuth() || {};
  const [dismissed, setDismissed] = useState(true);

  // Session 60.24 · Extract the two primitives the effect actually cares
  // about so react-hooks/exhaustive-deps can statically verify the
  // dependency list. Preserves the original behavior: (a) do nothing until
  // we have a real user, (b) hide the banner if the user has a postal_code,
  // (c) otherwise respect the 14-day localStorage dismiss window.
  const userId = user?.user_id;
  const userPostalCode = user?.postal_code;

  useEffect(() => {
    if (!userId) return;
    if (userPostalCode) { setDismissed(true); return; }
    setDismissed(isRecentlyDismissed());
  }, [userId, userPostalCode]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(LS_KEY, String(Date.now())); } catch {}
    setDismissed(true);
  }, []);

  if (!user || user.postal_code || dismissed) return null;

  const isCompact = variant === "compact";
  return (
    <div
      data-testid="zip-prompt-banner"
      role="region"
      aria-label="Add your ZIP code"
      style={{
        marginBottom: 18,
        padding: isCompact ? "12px 14px" : "14px 18px",
        borderRadius: 12,
        border: "1px solid rgba(184,145,58,0.32)",
        background: "linear-gradient(135deg, rgba(184,145,58,0.10) 0%, rgba(9,14,32,0.02) 100%)",
        display: "flex",
        gap: 14,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1 1 260px", minWidth: 220 }}>
        <div
          style={{
            fontSize: 11, letterSpacing: "0.16em",
            textTransform: "uppercase", color: "#B8913A", fontWeight: 700,
          }}
        >
          Find members &amp; workspaces near you
        </div>
        <div
          style={{
            fontSize: isCompact ? 13 : 14, lineHeight: 1.55, marginTop: 4,
            opacity: 0.85,
          }}
        >
          Add your ZIP code to personalize what&#39;s nearby.
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Link
          to="/profile#zip"
          data-testid="zip-prompt-cta"
          className="m-btn-gold"
          style={{
            textDecoration: "none",
            fontSize: 12, letterSpacing: "0.08em",
            textTransform: "uppercase", padding: "8px 16px",
          }}
        >
          Add ZIP Code
        </Link>
        <button
          type="button"
          onClick={dismiss}
          data-testid="zip-prompt-dismiss"
          aria-label="Dismiss ZIP prompt"
          style={{
            background: "transparent",
            border: "none",
            color: "rgba(0,0,0,0.5)",
            fontSize: 12, cursor: "pointer",
            padding: "6px 10px",
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
